/**
 * @file CreditAgainstBill.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-13
 * @modified 2026-10-13
 * @description Add / Edit Supplier Credit's notice for a credit raised against
 *              ONE bill (Jade, 13 Oct 2026). It asks the two things saving the
 *              credit does with that bill — clear its query (when it is
 *              queried), and allocate the credit to it — both ticked unless
 *              the user unticks them, and says what will happen: how much is
 *              allocated, whether the bill is then paid or part paid, and that
 *              it happens once the credit (and the bill) are in Xero; or why
 *              nothing will be. The server works it out again on save
 *              (supplierCreditService.recordAgainstBill), from GET
 *              /api/bills/:id/credit-against (`preview`).
 */

import React from 'react';
import TickCheckbox from './TickCheckbox';
import { againstBillSummary, autoAllocationText } from '../utils/supplierCredits';

/**
 * @param {object} props
 * @param {object|null} props.bill — the one bill the credit relates to, or null
 * @param {boolean} props.several — the credit names more than one bill
 * @param {object|null} props.preview — GET /api/bills/:id/credit-against's answer ({ loading } / { error })
 * @param {object|null} props.saved — an edited credit's xero_push_info.auto_allocation
 * @param {boolean} props.inXero — the credit is in Xero already (nothing is asked then)
 * @param {number} props.creditTotal — the credit's total as typed
 * @param {boolean} props.clearQuery
 * @param {(v: boolean) => void} props.onClearQuery
 * @param {boolean} props.allocate
 * @param {(v: boolean) => void} props.onAllocate
 */
function CreditAgainstBill({ bill, several, preview, saved, inXero, creditTotal, clearQuery, onClearQuery, allocate, onAllocate }) {
  const savedText = autoAllocationText(saved);
  if (inXero) {
    return savedText ? (
      <div className="form-group credit-against">
        <p className="credit-against-text"><span className="material-symbols-rounded">credit_score</span>{savedText}</p>
      </div>
    ) : null;
  }
  if (several) {
    return (
      <div className="form-group credit-against">
        <p className="credit-against-text">
          <span className="material-symbols-rounded">info</span>
          This credit names more than one bill, so it is not allocated automatically — use &quot;Allocate to bills…&quot; once it is in Xero.
        </p>
      </div>
    );
  }
  if (!bill) return null;
  const label = bill.supplier_reference || bill.bill_number || `Bill #${bill.id}`;
  if (!preview || preview.loading) {
    return (
      <div className="form-group credit-against">
        <p className="credit-against-text"><span className="material-symbols-rounded">hourglass_empty</span>Checking {label}…</p>
      </div>
    );
  }
  if (preview.error) {
    return (
      <div className="form-group credit-against is-warn">
        <p className="credit-against-text"><span className="material-symbols-rounded">warning</span>{label} could not be checked ({preview.error}). The credit can still be saved; allocate it with &quot;Allocate to bills…&quot;.</p>
      </div>
    );
  }
  const plan = preview.plan || {};
  const summary = againstBillSummary({ preview, creditTotal, clearQuery, allocate });
  const queryBlock = plan.clear_query?.block || null;
  // Why the allocation itself cannot be offered at all (not the query, not the user's choice).
  const allocBlock = !plan.allocate && plan.code && plan.code !== 'BILL_QUERIED' ? plan.reason : null;
  return (
    <div className={`form-group credit-against${summary.tone === 'warn' ? ' is-warn' : ''}`}>
      <label>Against {label}</label>
      <div className="credit-against-choices">
        {plan.queried && (
          <TickCheckbox
            id="credit-clear-query"
            checked={!queryBlock && clearQuery}
            disabled={!!queryBlock}
            onChange={(e) => onClearQuery(e.target.checked)}
            label={queryBlock
              ? `${label} is queried — its query cannot be cleared from here (${queryBlock})`
              : `${label} is queried — clear the query when this credit is saved`}
          />
        )}
        <TickCheckbox
          id="credit-allocate-bill"
          checked={!allocBlock && allocate}
          disabled={!!allocBlock}
          onChange={(e) => onAllocate(e.target.checked)}
          label={`Allocate this credit to ${label}`}
        />
      </div>
      {summary.text && (
        <p className="credit-against-text">
          <span className="material-symbols-rounded">{summary.allocates ? 'credit_score' : 'info'}</span>
          {summary.text}
        </p>
      )}
      {savedText && saved?.status !== 'pending' && <p className="field-hint">{savedText}</p>}
    </div>
  );
}

export default CreditAgainstBill;
